import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CardHeader, Container, Grid, MenuItem, TextField, Typography } from "@mui/material";
import MaterialTable from "material-table";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";

const DEFAULT_DORMANT_MONTHS = 6;
const customerTypeOptions = ["All", "End User", "Dealer"];

const formatDate = (value) => {
    if (!value) return "-";

    const parsed = moment(value);
    if (!parsed.isValid()) return value;

    return parsed.format("DD MMM YYYY");
};

const mapCustomerTypeToSegment = (value) => {
    if (value === "End User") return "end_users";
    if (value === "Dealer") return "dealers";
    return "all";
};

const parseMonthYear = (monthYearLabel) => {
    const parsed = moment(monthYearLabel, ["MMMM YYYY", "MMM YYYY"], true);
    if (parsed.isValid()) {
        return {
            month: parsed.month() + 1,
            year: parsed.year(),
        };
    }

    return {
        month: moment().month() + 1,
        year: moment().year(),
    };
};

export const DormantCustomersListPage = () => {
    const classes = CustomStyles;
    const navigate = useNavigate();
    const { state } = useLocation();
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const monthYear = state?.monthYear || moment().format("MMMM YYYY");
    const fallbackPeriod = parseMonthYear(monthYear);
    const month = Number(state?.month) || fallbackPeriod.month;
    const year = Number(state?.year) || fallbackPeriod.year;
    const countryId = Number(state?.countryId) || Number(identity?.user?.country_id);
    const dormantMonths = Number(state?.dormantMonths) || DEFAULT_DORMANT_MONTHS;

    const [searchKeyword, setSearchKeyword] = useState("");
    const [customerType, setCustomerType] = useState("All");
    const searchInputRef = useRef(null);

    const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");
    const [appliedCustomerType, setAppliedCustomerType] = useState("All");
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [summary, setSummary] = useState({
        totalCount: Number(state?.totalDormant) || 0,
        endUsersCount: Number(state?.endUsers) || 0,
        dealersCount: Number(state?.dealers) || 0,
    });

    const fetchDormantCustomers = useCallback(async () => {
        if (!countryId) {
            setData([]);
            setCount(0);
            return;
        }

        setIsLoading(true);
        try {
            const params = {
                month,
                year,
                dormant_months: dormantMonths,
                segment: mapCustomerTypeToSegment(appliedCustomerType),
                page: page + 1,
                limit: perPage,
            };

            if (appliedSearchKeyword.trim()) {
                params.search = appliedSearchKeyword.trim();
            }

            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${countryId}/dormant-customers`, {
                params,
            });

            const payload = response?.data?.data || {};
            const rows = (payload.data || []).map((item) => ({
                code: item.customerCode,
                company_name: item.customerName,
                customer_type: item.customerType,
                customer_created_date: formatDate(item.customerCreatedDate),
                last_sale_date: formatDate(item.lastSaleDate),
            }));

            setData(rows);
            setCount(Number(payload.total || 0));
            setSummary((prevSummary) => ({
                ...prevSummary,
                totalCount: Number(payload.total || 0),
            }));
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            const message = error?.response?.data?.message || "Unable to fetch dormant customer list.";
            Swal.fire({
                icon: "error",
                title: "Failed to Load Data",
                text: message,
                confirmButtonColor: "#fe7320",
            });
        } finally {
            setIsLoading(false);
        }
    }, [countryId, month, year, dormantMonths, appliedCustomerType, page, perPage, appliedSearchKeyword, logout]);

    useEffect(() => {
        fetchDormantCustomers();
    }, [fetchDormantCustomers]);

    const columns = [
        {
            title: "Code",
            field: "code",
            cellStyle: { width: 130, maxWidth: 130 },
            headerStyle: { width: 130, maxWidth: 130 },
        },
        {
            title: "Company Name",
            field: "company_name",
        },
        {
            title: "Customer Type",
            field: "customer_type",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
        },
        {
            title: "Customer Created Date",
            field: "customer_created_date",
            cellStyle: { width: 190, maxWidth: 190 },
            headerStyle: { width: 190, maxWidth: 190 },
        },
        {
            title: "Last Sale Date",
            field: "last_sale_date",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
        },
    ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                <MaterialTable
                    title={`Dormant Customers List: ${monthYear}`}
                    icons={Icons}
                    columns={columns}
                    data={data}
                    page={page}
                    totalCount={count}
                    isLoading={isLoading}
                    style={{ padding: "24px" }}
                    options={{
                        pageSize: perPage,
                        pageSizeOptions: [10, 20, 30, 50],
                        search: false,
                        filtering: false,
                        sorting: false,
                        actionsColumnIndex: -1,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        maxBodyHeight: "600px",
                        paginationType: "stepped",
                        padding: "dense",
                        draggable: false,
                        rowStyle: () => ({ backgroundColor: "#ffffff" }),
                    }}
                    localization={{
                        pagination: {
                            labelRowsPerPage: "",
                            labelDisplayedRows: "{from}-{to} of {count}",
                            labelRowsSelect: "rows",
                        },
                        toolbar: {
                            nRowsSelected: "{0} row(s) selected",
                        },
                        header: {
                            actions: "Actions",
                        },
                        body: {
                            emptyDataSourceMessage: "No data to display",
                        },
                    }}
                    onChangePage={(nextPage) => setPage(nextPage)}
                    onChangeRowsPerPage={(nextPerPage) => {
                        setPerPage(nextPerPage);
                        setPage(0);
                    }}
                    components={{
                        Toolbar: () => (
                            <>
                                <Grid container spacing={2} sx={{ px: 0.1, pb: 1.5 }}>
                                    <Grid item xs={12} sm={8}>
                                        <CardHeader
                                            sx={{ p: 0 }}
                                            title={
                                                <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                    Dormant Customers List: {monthYear}
                                                </Typography>
                                            }
                                            subheader={
                                                <Typography sx={{ mt: 0.8, color: "#2e3d4f", fontSize: "1rem" }}>
                                                    Total Dormant: {summary.totalCount} Accounts ({summary.endUsersCount} End Users | {summary.dealersCount} Dealers) | Dormant Months: {dormantMonths}
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                        <Button color="error" onClick={() => navigate(-1)}>
                                            Back
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mt: 1.2, pb: 2 }}>
                                    <Box sx={{ flex: "1 1 320px", minWidth: 260, maxWidth: 420 }}>
                                        <TextField
                                            autoComplete="off"
                                            fullWidth
                                            size="small"
                                            label="Search by Name/Code"
                                            autoFocus={searchInputRef.current === document.activeElement}
                                            inputRef={searchInputRef}
                                            value={searchKeyword}
                                            onChange={(event) => setSearchKeyword(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: "0 0 220px", minWidth: 220 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Customer Type"
                                            value={customerType}
                                            onChange={(event) => setCustomerType(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {customerTypeOptions.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 130px", minWidth: 130 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            sx={{ height: "40px", whiteSpace: "nowrap" }}
                                            onClick={() => {
                                                setAppliedSearchKeyword(searchKeyword);
                                                setAppliedCustomerType(customerType);
                                                setPage(0);
                                            }}
                                            disabled={isLoading || !countryId}
                                        >
                                            Show
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        ),
                    }}
                />
            </Container>
        </div>
    );
};
